import { useState } from 'react';
import { useParams } from 'react-router';
import type { Organization } from '@gatecheckpro/contracts';
import {
  Button,
  Card,
  Field,
  LoadingRegion,
  Notice,
  Status,
  presentation,
  VERIFICATION_STATES,
} from '@gatecheckpro/design-system';
import { api } from '../lib/api.ts';
import { useResource } from '../lib/useResource.ts';
import { FailureNotice } from '../lib/refusal.tsx';

interface HistoryEntry {
  readonly occurredAt: string;
  readonly fromState: string;
  readonly toState: string;
  readonly actorDisplayName: string | null;
  readonly reasonCode: string | null;
  readonly note: string | null;
}

/**
 * Organizer verification.
 *
 * The state is shown with its history, because "why are we still not verified"
 * is the question this page exists to answer. A single badge with no trail is
 * exactly the design that generates support tickets.
 */
export function VerificationPage() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const base = `/api/v1/organizer/organizations/${organizationId ?? ''}`;
  const { resource, reload } = useResource<Organization>(
    organizationId === undefined ? null : base,
  );
  const history = useResource<{ history: HistoryEntry[] }>(
    organizationId === undefined ? null : `${base}/verification/history`,
  );

  const [form, setForm] = useState({
    legalName: '',
    registrationNumber: '',
    representativeName: '',
    representativeEmail: '',
    representativePhone: '',
  });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.request(`${base}/verification`, {
        method: 'POST',
        body: {
          legalName: form.legalName,
          registrationNumber: form.registrationNumber,
          representativeName: form.representativeName,
          representativeEmail: form.representativeEmail,
          representativePhone: { number: form.representativePhone, region: 'NG' },
        },
      });
      reload();
      history.reload();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  if (resource.status === 'loading') return <LoadingRegion label="Loading verification" />;
  if (resource.status === 'failed') return <FailureNotice error={resource.error} />;

  const organization = resource.data;
  const state = presentation(VERIFICATION_STATES, organization.verification.state);
  const canSubmit =
    organization.verification.state === 'UNVERIFIED' ||
    organization.verification.state === 'REJECTED';

  return (
    <div className="gc-stack gc-stack--loose">
      <Card title="Verification status" eyebrow="CABS onboarding">
        <div className="gc-stack">
          <div className="gc-row">
            <Status
              tone={state.tone}
              glyph={state.glyph}
              label={state.label}
              title={state.meaning}
            />
          </div>
          <p className="gc-lede">{state.meaning}</p>

          {organization.verification.reasonCode !== null && (
            <Notice
              tone={organization.verification.state === 'REJECTED' ? 'critical' : 'info'}
              title={`Reason: ${organization.verification.reasonCode}`}
            >
              {organization.verification.reviewerNote ?? 'No further note was recorded.'}
            </Notice>
          )}

          {/*
            Said plainly, because an organizer will otherwise assume verification
            gates something it does not yet gate.
          */}
          <Notice tone="info" title="What verification affects">
            Nothing is blocked by verification in this release — there is no selling yet. Taking
            money and receiving settlement will require it, so submitting now means the review is
            done before it matters.
          </Notice>
        </div>
      </Card>

      {canSubmit && (
        <Card title="Submit for verification" eyebrow="Business identity">
          <form className="gc-stack" onSubmit={(event) => void submit(event)}>
            <FailureNotice error={error} />

            <Field
              label="Registered legal name"
              required
              value={form.legalName}
              onChange={(event) => setForm({ ...form, legalName: event.target.value })}
            />
            <Field
              label="Registration number"
              required
              hint="Your CAC registration number, or the equivalent where you are registered."
              value={form.registrationNumber}
              onChange={(event) => setForm({ ...form, registrationNumber: event.target.value })}
            />
            <Field
              label="Authorised representative"
              required
              value={form.representativeName}
              onChange={(event) => setForm({ ...form, representativeName: event.target.value })}
            />
            <Field
              label="Representative email"
              type="email"
              required
              value={form.representativeEmail}
              onChange={(event) => setForm({ ...form, representativeEmail: event.target.value })}
            />
            <Field
              label="Representative phone"
              required
              hint="Nigerian number. Stored in international format."
              placeholder="08031234567"
              value={form.representativePhone}
              onChange={(event) => setForm({ ...form, representativePhone: event.target.value })}
            />

            <Button type="submit" variant="primary" busy={busy}>
              Submit for review
            </Button>
          </form>
        </Card>
      )}

      <Card title="History" eyebrow="Every state change, kept">
        {history.resource.status === 'loading' && <LoadingRegion label="Loading history" />}
        {history.resource.status === 'failed' && <FailureNotice error={history.resource.error} />}
        {history.resource.status === 'loaded' &&
          (history.resource.data.history.length === 0 ? (
            <p className="gc-muted">Nothing has happened yet.</p>
          ) : (
            <div className="gc-table-scroll" style={{ border: 0 }}>
              <table className="gc-table">
                <thead>
                  <tr>
                    <th scope="col">When</th>
                    <th scope="col">Change</th>
                    <th scope="col">By</th>
                    <th scope="col">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {history.resource.data.history.map((entry) => (
                    <tr key={`${entry.occurredAt}-${entry.toState}`}>
                      <td className="gc-mono">{new Date(entry.occurredAt).toLocaleString()}</td>
                      <td>
                        {presentation(VERIFICATION_STATES, entry.fromState).label} →{' '}
                        <strong>{presentation(VERIFICATION_STATES, entry.toState).label}</strong>
                      </td>
                      <td>{entry.actorDisplayName ?? '—'}</td>
                      <td>
                        <div className="gc-stack gc-stack--tight">
                          <span>{entry.reasonCode ?? '—'}</span>
                          {entry.note !== null && <span className="gc-muted">{entry.note}</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
      </Card>
    </div>
  );
}
